/* Learning Bugs search panel */
(() => {
  if (customElements.get('lb-search')) return;

  class LbSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.lbs__input');
      this.body = this.querySelector('.lbs__body');
      this.clearBtn = this.querySelector('.lbs__clear');
      this.url = this.dataset.url;
      this.cache = new Map();
      this.timer = null;
      this.ctrl = null;
      this.lastFocus = null;

      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-lb-search-open], .show-search-link');
        if (!t || this.contains(t)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open(t);
      }, true);

      window.addEventListener('resize', () => { if (!this.hidden && this.dropFrom) this.place(); });
      this.querySelectorAll('[data-lbs-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.onInput(); this.input.focus(); });
      this.querySelector('form').addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
        const active = this.body.querySelector('[role="option"].is-active');
        if (active) { e.preventDefault(); window.location.href = active.href; }
      });
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      const bar = trigger && trigger.closest ? trigger.closest('.lb-hs') : null;
      /* phones and small tablets (820px and under) use the full-screen sheet, which has a Close button */
      const wide = !window.matchMedia || window.matchMedia('(min-width: 821px)').matches;
      this.dropFrom = wide && bar && bar.getBoundingClientRect().width > 120 ? bar : null;
      this.classList.toggle('lbs--drop', !!this.dropFrom);
      this.hidden = false;
      document.documentElement.classList.add('lbs-open');
      this.watchViewport(true);
      if (this.dropFrom) this.place();
      /* focus now, inside the tap, so iOS opens the keyboard (a later focus() is ignored by iOS) */
      this.input.focus({ preventScroll: true });
      requestAnimationFrame(() => this.classList.add('is-open'));
      if (!this.input.value.trim()) this.showEmpty();
      setTimeout(() => this.input.focus({ preventScroll: true }), 30);
    }

    place() {
      if (!this.dropFrom) return;
      const r = this.dropFrom.getBoundingClientRect();
      const vw = document.documentElement.clientWidth, vh = window.innerHeight;
      const top = Math.round(r.bottom + 10);
      const left = vw < 820 ? Math.max(8, Math.round(r.left)) : Math.round(r.left);
      const w = vw < 820 ? vw - left * 2 : Math.min(1040, vw - left - 24);
      const px = (n) => Math.round(n) + 'px';
      this.style.setProperty('--lbs-bt', px(r.top));
      this.style.setProperty('--lbs-bl', px(r.left));
      this.style.setProperty('--lbs-bw', px(r.width));
      this.style.setProperty('--lbs-bh', px(r.height));
      this.style.setProperty('--lbs-t', px(top));
      this.style.setProperty('--lbs-l', px(left));
      this.style.setProperty('--lbs-w', px(w));
      this.style.setProperty('--lbs-mh', px(Math.max(240, vh - top - 16)));
    }

    /* keep the sheet inside the visible screen when the phone keyboard is open (iOS does not shrink innerHeight) */
    watchViewport(on) {
      const vv = window.visualViewport;
      if (!vv) return;
      if (!this.onVV) this.onVV = () => {
        this.style.setProperty('--lbs-vh', Math.round(vv.height) + 'px');
        this.style.setProperty('--lbs-vt', Math.round(vv.offsetTop) + 'px');
      };
      if (on) { this.onVV(); vv.addEventListener('resize', this.onVV); vv.addEventListener('scroll', this.onVV); }
      else { vv.removeEventListener('resize', this.onVV); vv.removeEventListener('scroll', this.onVV); }
    }

    close() {
      this.watchViewport(false);
      this.classList.remove('is-open');
      document.documentElement.classList.remove('lbs-open');
      setTimeout(() => { this.hidden = true; }, 180);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    tpl(cls) {
      const t = this.querySelector(cls);
      return t ? t.content.cloneNode(true) : document.createDocumentFragment();
    }

    showEmpty() {
      this.body.innerHTML = '';
      this.body.appendChild(this.tpl('.lbs__empty-tpl'));
      this.clearBtn.hidden = true;
    }

    onInput() {
      const q = this.input.value.trim();
      this.clearBtn.hidden = !this.input.value;
      clearTimeout(this.timer);
      if (!q) { if (this.ctrl) this.ctrl.abort(); this.classList.remove('is-loading'); this.showEmpty(); return; }
      this.timer = setTimeout(() => this.fetchResults(q), 180);
    }

    fetchResults(q) {
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      this.classList.add('is-loading');
      const p = new URLSearchParams({
        q,
        section_id: 'lb-predictive-search',
        'resources[type]': 'product,collection,query,article,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
      });
      fetch(`${this.url}?${p.toString()}`, { signal: this.ctrl.signal })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((html) => {
          const res = new DOMParser().parseFromString(html, 'text/html').querySelector('.lbs-res');
          const frag = document.createDocumentFragment();
          if (res) frag.appendChild(res);
          this.cache.set(q, frag);
          if (this.input.value.trim() === q) this.render(frag);
        })
        .catch((err) => { if (err.name !== 'AbortError') { this.classList.remove('is-loading'); } });
    }

    render(frag) {
      this.classList.remove('is-loading');
      this.body.innerHTML = '';
      const node = frag.cloneNode(true);
      this.body.appendChild(node);
      if (this.body.querySelector('[data-lbs-none]')) {
        this.body.appendChild(this.tpl('.lbs__empty-tpl'));
        this.body.appendChild(this.tpl('.lbs__help-tpl'));
      }
      this.body.scrollTop = 0;
    }

    onKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const opts = [...this.body.querySelectorAll('[role="option"], .lbs-chip, .lbs-age, .lbs-brand')];
      if (!opts.length) return;
      e.preventDefault();
      let i = opts.findIndex((o) => o.classList.contains('is-active'));
      if (i > -1) opts[i].classList.remove('is-active');
      i = e.key === 'ArrowDown' ? (i + 1) % opts.length : (i <= 0 ? opts.length - 1 : i - 1);
      opts[i].classList.add('is-active');
      opts[i].scrollIntoView({ block: 'nearest' });
    }
  }

  customElements.define('lb-search', LbSearch);
})();
